import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

type ConfirmVariant = 'danger' | 'warning' | 'info';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

interface ConfirmDialogContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

interface PendingConfirmRequest {
  id: number;
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | undefined>(undefined);

export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const idRef = useRef(1);
  const queueRef = useRef<PendingConfirmRequest[]>([]);
  const [activeRequest, setActiveRequest] = useState<PendingConfirmRequest | null>(null);

  const openNextIfNeeded = useCallback(() => {
    if (activeRequest || queueRef.current.length === 0) return;
    const next = queueRef.current.shift() || null;
    setActiveRequest(next);
  }, [activeRequest]);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      queueRef.current.push({
        id: idRef.current++,
        options,
        resolve,
      });
      setTimeout(() => {
        openNextIfNeeded();
      }, 0);
    });
  }, [openNextIfNeeded]);

  const closeDialog = useCallback((result: boolean) => {
    if (!activeRequest) return;
    activeRequest.resolve(result);
    setActiveRequest(null);

    setTimeout(() => {
      if (queueRef.current.length > 0) {
        const next = queueRef.current.shift() || null;
        setActiveRequest(next);
      }
    }, 0);
  }, [activeRequest]);

  const contextValue = useMemo(() => ({ confirm }), [confirm]);

  const variant = activeRequest?.options.variant || 'warning';
  const confirmButtonClass =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
      : variant === 'warning'
      ? 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}

      {activeRequest && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {activeRequest.options.title || 'Confirmar acción'}
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-sm text-gray-700 whitespace-pre-line">{activeRequest.options.message}</p>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => closeDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {activeRequest.options.cancelText || 'Cancelar'}
              </button>
              <button
                onClick={() => closeDialog(true)}
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 ${confirmButtonClass}`}
              >
                {activeRequest.options.confirmText || 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirm = (): ConfirmDialogContextValue => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirm debe usarse dentro de ConfirmDialogProvider');
  }
  return context;
};
